// Settings › Audit log (design/screens/audit-log.html): filters, keyset paging and an export that opens the signed link.
// Rows are the contract's AuditEntryView (packages/contracts/src/governance.ts): the actor — a user with a Control Id, or the
// system — the before/after diff with the reader's redactions applied, the step-up authoriser of the same request, the source.
// A 404 is an honest empty state, a 403 the no-access panel.
import { AUDIT_ACTIONS, AUDIT_SOURCES, controlIdDisplay, type AuditActionCode, type AuditEntryView, type AuditSourceCode } from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { governance, users, type AuditFilter } from "../api/endpoints";
import { describeError, isApiError } from "../api/problem";
import { startDownload } from "../lib/download";
import { formatDate, formatDateTime, formatTime, localDate, localDayWindow } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { useWritesEnabled } from "../shell/OfflineBanner";
import { Alert, Chip, Empty, ErrorAlert, Loading, Pager, Panel, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { NoAccessPanel, PageHead, SettingsTabs } from "./common";

/** The entity types Sprint 1 writes (the audited tables), as the filter offers them; later sprints extend the list. */
const ENTITY_TYPES: readonly [string, string][] = [
  ["user", "User"], ["user_branch_assignment", "Branch assignment"], ["session", "Session"], ["branch", "Branch"], ["branch_supplier_account", "Supplier account"],
  ["organisation", "Organisation"], ["setting", "Setting"], ["feature_flag", "Feature flag"], ["pin_protected_action", "PIN-protected action"],
  ["privacy_document", "Privacy document"], ["dsar_request", "Data-subject request"], ["document_sequence", "Document sequence"],
  ["audit_log", "Audit export"], ["data_export", "Sealed export"], ["file", "File download"],
];
const ENTITY_LABEL = new Map(ENTITY_TYPES);
const entityLabel = (t: string): string => ENTITY_LABEL.get(t) ?? t.replace(/_/g, " ");

const ACTION_LABEL: Record<AuditActionCode, string> = {
  Create: "Created", Update: "Updated", Delete: "Deleted", Void: "Voided", View: "Viewed", Export: "Exported", Print: "Printed",
  Login: "Signed in", LoginFailed: "Sign-in failed", Logout: "Signed out", Lock: "Screen locked", Unlock: "Screen unlocked",
  PasswordReset: "Password reset", TotpChange: "TOTP changed", PinChange: "PIN changed", PinLock: "PIN locked",
  RoleChange: "Role changed", BranchAssign: "Branches assigned", SettingsChange: "Settings changed", FlagChange: "Feature flag changed",
  SupportGrant: "Support access granted", Invite: "Invited", Deactivate: "Deactivated", Reactivate: "Reactivated",
  BranchSwitch: "Branch switched", HandoverEnter: "Handover started", HandoverExit: "Handover ended", RecoveryCodeUse: "Recovery code used",
};
/**
 * An administrator's Clear lock is audited `Unlock`, as a user's own unlock at the lock screen is — the audit action list is held
 * by a CHECK constraint, so a separate value waits for a migration. It is told apart by what it records: a clearance names what
 * it cleared (`pinFailuresCleared` in every one) and is made by someone other than the user (UC-IDENTITY-008 A4; testing of
 * 12 Oct 2026, finding 28). The actor column names the administrator.
 */
const isLockClearance = (r: AuditEntryView): boolean =>
  r.action === "Unlock" && r.entityType === "user" && (r.diff?.after?.pinFailuresCleared !== undefined || (r.actor.type === "User" && r.entityId !== null && r.actor.id !== `user:${r.entityId}`));
const actionLabel = (r: AuditEntryView): string => (isLockClearance(r) ? "Lock cleared" : ACTION_LABEL[r.action]);
/** Refusals and locks read as evidence, in the prototype's crit chip. */
const REFUSALS: ReadonlySet<AuditActionCode> = new Set(["LoginFailed", "PinLock"]);
const SOURCE_LABEL: Record<AuditSourceCode, string> = { UI: "UI", API: "API", Import: "Import", AccountingSync: "Accounting sync", Cron: "Scheduled", Workflow: "Workflow" };
const ACTOR_LABEL: Record<AuditEntryView["actor"]["type"], string> = { User: "User", System: "System", PlatformAdmin: "Platform admin", FranchisorUser: "Franchisor" };

/** The toolbar's state: the filters with `from`/`to` as branch-local calendar dates; the query sends the days' instants. */
type DayFilter = Omit<AuditFilter, "from" | "to"> & { from?: string | undefined; to?: string | undefined };

export function AuditLog() {
  const s = useSessionView();
  const online = useWritesEnabled();
  const today = localDate(new Date());
  const [f, setF] = useState<DayFilter>({ from: today, to: today, branchId: s?.activeBranchId ?? undefined });
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const { from, to, ...rest } = f;
  const query: AuditFilter = { ...rest, from: localDayWindow(from)?.from, to: localDayWindow(to)?.to };
  const q = useQuery({ queryKey: ["audit", query, cursor ?? ""], queryFn: () => governance.audit(query, { limit: 50, cursor }) });
  const userList = useQuery({ queryKey: ["users", "all"], queryFn: () => users.list({ limit: 200 }), enabled: can(s, "identity.user.read") });
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const set = (k: keyof DayFilter) => (e: { target: { value: string } }) => { setCursors([]); setF((p) => ({ ...p, [k]: e.target.value || undefined }) as DayFilter); };
  const branchName = (id: string | null | undefined) => s?.switchableBranches.find((b) => b.id === id)?.name ?? null;
  const rows = q.data?.data.items ?? [];
  const notBuilt = q.isError && isApiError(q.error) && q.error.code === "NOT_FOUND";
  const sameDay = !!from && from === to;

  const exportCsv = async () => {
    setExporting(true);
    setNotice(null);
    try {
      const { data } = await governance.auditExport(query, newIdempotencyKey());
      startDownload(data.downloadUrl, data.fileName);
      setNotice(<Alert tone="ok"><b>Export ready.</b> <span className="mono">{data.fileName}</span> · {data.rowCount} row{data.rowCount === 1 ? "" : "s"} · the download has started; <a href={data.downloadUrl} download={data.fileName}>download it again</a> until {formatTime(data.expiresAt)}. The export itself is audited.</Alert>);
    } catch (err) {
      setNotice(<Alert tone="crit"><b>Export failed.</b> {describeError(err)}</Alert>);
    } finally { setExporting(false); }
  };

  return (
    <section className="screen" id="s-audit-log">
      <div className="content">
        <PageHead
          title="Settings — Audit log"
          sub={`Every read, edit, export, sign-in, unlock and branch switch · ${branchName(f.branchId) ?? "all branches"} · ${sameDay ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`}`}
          actions={can(s, "governance.audit.export") && <button className="btn" type="button" disabled={exporting || !online || notBuilt} onClick={() => void exportCsv()}>Export CSV<span className="small faint">logged</span></button>}
        />
        <SettingsTabs />
        <Alert tone="info" actions={can(s, "governance.control_log.read") && <Link className="btn sm" to="/control-log">Open Control log</Link>}>
          <b>PIN-authorised actions live in the Control log</b> — discounts, price overrides, voids, credit notes, write-offs and cash-up variances, with the authorising Control ID. This log holds everything else.
        </Alert>
        {notice}
        <div className="toolbar">
          <TextInput type="date" aria-label="From" boxClass="w-range" value={from ?? ""} onChange={set("from")} mono />
          <TextInput type="date" aria-label="To" boxClass="w-range" value={to ?? ""} onChange={set("to")} mono />
          <Select aria-label="User" boxClass="w-sel" value={f.userId ?? ""} onChange={set("userId")}>
            <option value="">User · All</option>
            {userList.data?.data.items.map((u) => <option key={u.id} value={u.id}>{u.displayName} {u.controlIdDisplay}</option>)}
          </Select>
          <Select aria-label="Entity" boxClass="w-sel" value={f.entityType ?? ""} onChange={set("entityType")}>
            <option value="">Entity · All</option>
            {ENTITY_TYPES.map(([t, label]) => <option key={t} value={t}>Entity · {label}</option>)}
          </Select>
          <Select aria-label="Action" boxClass="w-sel" value={f.action ?? ""} onChange={set("action")}>
            <option value="">Action · All</option>
            {AUDIT_ACTIONS.map((a) => <option key={a} value={a}>Action · {ACTION_LABEL[a]}</option>)}
          </Select>
          <Select aria-label="Source" boxClass="w-sel" value={f.source ?? ""} onChange={set("source")}>
            <option value="">Source · All</option>
            {AUDIT_SOURCES.map((t) => <option key={t} value={t}>Source · {SOURCE_LABEL[t]}</option>)}
          </Select>
          <Select aria-label="Branch" boxClass="w-branch" value={f.branchId ?? ""} onChange={set("branchId")}>
            <option value="">Branch · All</option>
            {s?.switchableBranches.map((b) => <option key={b.id} value={b.id}>Branch · {b.name}</option>)}
          </Select>
          <span className="right small faint">{q.data ? `${rows.length} entries shown · newest first` : ""}</span>
        </div>
        {q.isPending && <Loading what="audit log" />}
        {q.isError && (isApiError(q.error) && q.error.code === "PERMISSION_DENIED" ? <NoAccessPanel page="Audit log" error={q.error} />
          : notBuilt ? <Panel><Empty title="The audit log is not available yet.">The governance routes land in Sprint 1; entries are being written already and will appear here.</Empty></Panel>
          : <ErrorAlert error={q.error} />)}
        {q.data && (
          <Panel tight foot={<>
            <span className="small faint">Entries are written once and never edited or deleted · before and after values kept on every edit</span>
            <Pager label={`Page ${cursors.length + 1} · newest first`} hasPrev={cursors.length > 0} hasNext={!!q.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, q.data!.data.nextCursor!])} />
          </>}>
            {rows.length === 0 ? <Empty title="No entries for these filters.">Widen the date range or clear a filter.</Empty> : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>When</th><th>User</th><th>Action</th><th>Entity</th><th>Detail</th><th>Source</th></tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="mono nw">{sameDay ? formatTime(r.occurredAt) : formatDateTime(r.occurredAt)}</td>
                        <td className="nw">{actorOf(r.actor)}</td>
                        <td><Chip tone={REFUSALS.has(r.action) ? "crit" : "neutral"}>{actionLabel(r)}</Chip></td>
                        <td className="nw">{entityLabel(r.entityType)}{r.entityId && <> <span className="mono faint">{r.entityId.slice(0, 10)}</span></>}</td>
                        <td>{detailOf(r, !f.branchId)}</td>
                        <td><Chip tone="neutral">{SOURCE_LABEL[r.source]}</Chip></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
        <div className="help"><Icon name="info" style={{ display: "inline", width: 12, height: 12, verticalAlign: -2 }} /> Filters apply to the branch and dates above; the export carries the same filters.</div>
      </div>
    </section>
  );
}

/** A user actor is the name and Control Id; the system, a platform admin or a franchisor user is its label (TEN-63). */
function actorOf(a: AuditEntryView["actor"]): ReactNode {
  if (a.type === "User") {
    return <>{a.displayName ?? <span className="mono">{a.id.replace(/^user:/, "").slice(0, 8)}</span>}{a.controlId !== null && <> <span className="mono">{controlIdDisplay(a.controlId)}</span></>}</>;
  }
  const job = a.type === "System" && a.id.startsWith("system:") ? a.id.slice("system:".length) : null;
  return <>{ACTOR_LABEL[a.type]}{job && <> <span className="small faint">{job}</span></>}</>;
}

const show = (v: unknown): string => (v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

/** The changed fields as `field: before → after`, the step-up authoriser and the redaction note; the request id when there is no diff. */
function detailOf(r: AuditEntryView, withBranch: boolean): ReactNode {
  const parts: ReactNode[] = [];
  if (r.diff && (r.diff.before || r.diff.after)) {
    const keys = [...new Set([...Object.keys(r.diff.before ?? {}), ...Object.keys(r.diff.after ?? {})])];
    const shown = keys.slice(0, 4).map((k) => `${k}: ${show(r.diff!.before?.[k])} → ${show(r.diff!.after?.[k])}`);
    parts.push(<span className="small">{shown.join(" · ")}{keys.length > 4 ? ` · +${keys.length - 4} more` : ""}</span>);
    const redacted = r.diff._redacted?.length ?? 0;
    if (redacted > 0) parts.push(<span className="small faint">{redacted} classified field{redacted === 1 ? "" : "s"} withheld</span>);
  }
  if (r.authorisedBy) parts.push(<span className="small">authorised by <span className="mono">{controlIdDisplay(r.authorisedBy.controlId)}</span> {r.authorisedBy.displayName}</span>);
  if (r.impersonation) parts.push(<span className="small faint">via support access</span>);
  if (withBranch && r.branch) parts.push(<span className="mono small faint">{r.branch.code}</span>);
  if (parts.length === 0) return <span className="faint">{r.requestId ? <span className="mono small">{r.requestId}</span> : "—"}</span>;
  return <>{parts.map((p, i) => <Fragment key={i}>{i > 0 ? " · " : ""}{p}</Fragment>)}</>;
}
